import type {
  SweetSpotEvaluationResult,
  SweetSpotParameterConfig,
  SweetSpotPointResult,
  SweetSpotQualityInterval,
} from './types';

type BuildOptions = {
  wellId: string;
  depthUnit?: 'm' | 'ft';
  sourceCurveIds: string[];
  parameterConfig: SweetSpotParameterConfig;
  targetStageLength?: number;
  clusterSpacing?: number;
  highQualityThreshold?: number;
  poorQualityThreshold?: number;
  ssiFormula?: string;
};

const finiteScore = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const round = (value: number, digits = 3) => Number(value.toFixed(digits));

function medianStep(points: SweetSpotPointResult[]): number {
  const steps = points.slice(1)
    .map((point, index) => point.depth - points[index].depth)
    .filter((step) => Number.isFinite(step) && step > 0)
    .sort((a, b) => a - b);
  return steps.length ? steps[Math.floor(steps.length / 2)] : 1;
}

function buildIntervals(
  points: SweetSpotPointResult[],
  predicate: (score: number) => boolean,
): SweetSpotQualityInterval[] {
  const step = medianStep(points);
  const intervals: SweetSpotQualityInterval[] = [];
  let active: Array<{ depth: number; score: number }> = [];

  const flush = () => {
    if (!active.length) return;
    intervals.push({
      top: round(active[0].depth, 2),
      bottom: round(active[active.length - 1].depth + step, 2),
      avgScore: round(active.reduce((sum, item) => sum + item.score, 0) / active.length),
    });
    active = [];
  };

  points.forEach((point, index) => {
    if (!finiteScore(point.ssi) || !predicate(point.ssi)) {
      flush();
      return;
    }
    const previous = points[index - 1];
    if (active.length && previous && point.depth - previous.depth > step * 1.8) flush();
    active.push({ depth: point.depth, score: point.ssi });
  });
  flush();
  return intervals;
}

function buildBreakpoints(points: SweetSpotPointResult[]) {
  return points.slice(1).flatMap((point, index) => {
    const previous = points[index];
    if (!finiteScore(point.ssi) || !finiteScore(previous.ssi)) return [];
    const change = point.ssi - previous.ssi;
    if (Math.abs(change) < 0.18) return [];
    return [{
      depth: round((point.depth + previous.depth) / 2, 2),
      reason: 'SSI ' + (change > 0 ? '升高 ' : '降低 ')
        + Math.abs(change).toFixed(2) + '，识别为非均质突变',
    }];
  });
}

function buildStageBoundaries(
  top: number,
  bottom: number,
  targetLength: number,
  breakpoints: Array<{ depth: number }>,
) {
  const safeLength = Math.max(20, targetLength);
  const boundaries = [top];
  let cursor = top;
  while (cursor + safeLength < bottom) {
    const ideal = cursor + safeLength;
    const nearby = breakpoints
      .filter((item) => item.depth > cursor + safeLength * 0.55 && item.depth < ideal + safeLength * 0.35)
      .sort((a, b) => Math.abs(a.depth - ideal) - Math.abs(b.depth - ideal))[0];
    const next = nearby?.depth ?? ideal;
    if (next <= cursor) break;
    boundaries.push(next);
    cursor = next;
  }
  boundaries.push(bottom);
  return boundaries.slice(0, -1).map((stageTop, index) => ({
    top: round(stageTop, 2),
    bottom: round(boundaries[index + 1], 2),
    reason: breakpoints.some((item) => Math.abs(item.depth - boundaries[index + 1]) < 0.01)
      ? '工程分段：边界对齐 SSI 非均质突变点'
      : '工程分段：按目标段长保持层段品质相近',
  }));
}

function buildClusterCandidates(points: SweetSpotPointResult[], spacing: number, minScore = 0.6) {
  const valid = points.filter((point) => finiteScore(point.ssi));
  const localPeaks = valid.filter((point, index) => {
    const previous = valid[index - 1]?.ssi;
    const next = valid[index + 1]?.ssi;
    return point.ssi! >= minScore
      && (!finiteScore(previous) || point.ssi! >= previous)
      && (!finiteScore(next) || point.ssi! >= next);
  }).sort((a, b) => b.ssi! - a.ssi!);

  const selected: SweetSpotPointResult[] = [];
  localPeaks.forEach((point) => {
    if (selected.every((item) => Math.abs(item.depth - point.depth) >= spacing)) selected.push(point);
  });
  return selected.sort((a, b) => a.depth - b.depth).map((point) => ({
    depth: round(point.depth, 2),
    score: round(point.ssi!),
    reason: '综合评分曲线局部高值，优先作为射孔簇候选位置',
  }));
}

export function buildSweetSpotEvaluationResult(
  rawPoints: SweetSpotPointResult[],
  options: BuildOptions,
): SweetSpotEvaluationResult | null {
  const points = rawPoints
    .filter((point) => Number.isFinite(point.depth))
    .slice()
    .sort((a, b) => a.depth - b.depth);
  const scored = points.filter((point) => finiteScore(point.ssi));
  if (!scored.length) return null;

  const top = scored[0].depth;
  const bottom = scored[scored.length - 1].depth + medianStep(scored);
  const breakpoints = buildBreakpoints(scored);
  return {
    wellId: options.wellId,
    intervalTop: round(top, 2),
    intervalBottom: round(bottom, 2),
    depthUnit: options.depthUnit ?? 'm',
    reservoirQualityCurve: points.flatMap((point) => finiteScore(point.rq)
      ? [{ depth: point.depth, value: round(point.rq) }] : []),
    completionQualityCurve: points.flatMap((point) => finiteScore(point.cq)
      ? [{ depth: point.depth, value: round(point.cq) }] : []),
    compositeSweetnessCurve: scored.map((point) => ({ depth: point.depth, value: round(point.ssi!) })),
    highQualityIntervals: buildIntervals(scored, (score) => score >= (options.highQualityThreshold ?? 0.6)),
    poorQualityIntervals: buildIntervals(scored, (score) => score < (options.poorQualityThreshold ?? 0.4)),
    heterogeneityBreakpoints: breakpoints,
    recommendedStageBoundaries: buildStageBoundaries(
      top, bottom, options.targetStageLength ?? 80, breakpoints,
    ),
    recommendedClusterCandidates: buildClusterCandidates(
      scored, options.clusterSpacing ?? 12, options.highQualityThreshold ?? 0.6,
    ),
    sourceCurveIds: Array.from(new Set(options.sourceCurveIds.filter(Boolean))),
    evaluationConfig: {
      parameters: options.parameterConfig,
      ssiFormula: options.ssiFormula ?? '0.5 * RQ + 0.5 * CQ（单组有效时使用该组评分）',
      highQualityThreshold: options.highQualityThreshold ?? 0.6,
      poorQualityThreshold: options.poorQualityThreshold ?? 0.4,
      heterogeneityThreshold: 0.18,
      targetStageLength: options.targetStageLength ?? 80,
      clusterSpacing: options.clusterSpacing ?? 12,
    },
    generatedAt: new Date().toISOString(),
  };
}
